import React from "react";
import CopyableContainer from "./CopyableContainer";
import LeagueTabHeading from "./LeagueTabHeading";
import Tooltip from "./Tooltip";
import "./styles/positionBreakdown.css";

const DISPLAY_CATEGORIES = ["QB", "RB", "WR", "TE", "D/ST", "K"];

const formatPoints = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "0.0";
    return numeric.toFixed(1);
};

const formatPercent = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "0%";
    return `${Math.round(numeric * 100)}%`;
};

const segmentClassName = (category) =>
    `position-segment position-segment--${category
        .toLowerCase()
        .replace("/", "")
        .replace(/[^a-z]/g, "")}`;

const bestEdgeCallout = (team, categories) => {
    const opponentBreakdown = team.metadata?.opponent_breakdown;
    if (!opponentBreakdown) return null;

    const ranked = categories
        .map((category) => ({
            category,
            delta: Number(team.breakdown?.[category] || 0) - Number(opponentBreakdown?.[category] || 0),
        }))
        .sort((a, b) => b.delta - a.delta);

    if (!ranked.length || !Number.isFinite(ranked[0].delta)) return null;
    return {
        label: "Best Edge",
        value: `${ranked[0].category} ${ranked[0].delta >= 0 ? "+" : ""}${ranked[0].delta.toFixed(1)}`,
    };
};

const buildCallouts = (team, categories) => {
    const callouts = [];
    if (team.metadata?.lineup_efficiency !== null && team.metadata?.lineup_efficiency !== undefined) {
        callouts.push({
            label: "Lineup Efficiency",
            value: formatPercent(team.metadata.lineup_efficiency),
        });
    }

    if (team.metadata?.bench_points !== null && team.metadata?.bench_points !== undefined) {
        callouts.push({
            label: "Bench Points",
            value: `${formatPoints(team.metadata.bench_points)} pts`,
        });
    }

    const trend = team.metadata?.trend_windows;
    if (trend?.available) {
        const delta = Number(trend.trailing_average_points || 0) - Number(trend.prior_average_points || 0);
        callouts.push({
            label: "Last 3 Avg",
            value: `${formatPoints(trend.trailing_average_points)} (${delta >= 0 ? "+" : ""}${delta.toFixed(1)})`,
        });
    }

    const edge = bestEdgeCallout(team, categories);
    if (edge) {
        callouts.push(edge);
    }

    return callouts.slice(0, 3);
};

const TeamPositionCards = ({ data, week, loading }) => {
    const categories = DISPLAY_CATEGORIES.filter((category) =>
        (data?.categories || DISPLAY_CATEGORIES).includes(category)
    );
    const teams = Array.isArray(data?.teams) ? data.teams : [];

    return (
        <CopyableContainer
            title={
                <LeagueTabHeading
                    eyebrow="Team Cards"
                    title={`Week ${week} snapshot`}
                    className="mb-0"
                />
            }
            fileName={`team-cards-week-${week}`}
        >
            <div className="position-breakdown-shell">
                {loading || !data ? (
                    <div className="position-cards-empty">Loading team cards...</div>
                ) : teams.length === 0 ? (
                    <div className="position-cards-empty">No team cards available.</div>
                ) : (
                    <div className="position-card-grid">
                        {teams.map((team) => {
                            const chips = Array.isArray(team.chips) ? team.chips.slice(0, 2) : [];
                            const callouts = buildCallouts(team, categories);
                            const lead = chips[0]?.reason || "Weekly position mix at a glance.";

                            return (
                                <article className="position-card" key={team.team_id || `${team.owner}-${team.team}`}>
                                    <div className="position-card-top">
                                        <div>
                                            <h4>{team.owner || team.team}</h4>
                                            {team.owner && team.team ? (
                                                <p>{team.team}</p>
                                            ) : null}
                                        </div>
                                        <div className="position-card-score">{formatPoints(team.total_points)}</div>
                                    </div>

                                    <p className="position-card-lead">{lead}</p>

                                    <div className="position-card-bar" aria-label={`${team.team} positional mix`}>
                                        {categories.map((category) => (
                                            <Tooltip
                                                key={category}
                                                label={`${category}: Contributed ${formatPercent(team.shares?.[category])} of team points.`}
                                                eyebrow="Position Share"
                                                wrapperClassName="relative block h-full"
                                                wrapperStyle={{
                                                    width: `${Math.max(Number(team.shares?.[category] || 0) * 100, 0)}%`,
                                                }}
                                                position="top"
                                            >
                                                <div
                                                    className={segmentClassName(category)}
                                                    style={{ width: "100%" }}
                                                />
                                            </Tooltip>
                                        ))}
                                    </div>

                                    <div className="position-card-legend">
                                        {categories.map((category) => (
                                            <span key={category}>
                                                <strong>{category}</strong> {formatPoints(team.breakdown?.[category])}
                                            </span>
                                        ))}
                                    </div>

                                    {chips.length > 0 && (
                                        <div className="position-card-chips">
                                            {chips.map((chip) => (
                                                <Tooltip
                                                    key={chip.key}
                                                    label={`${chip.label}: ${chip.reason}`}
                                                    eyebrow="Chip Insight"
                                                >
                                                    <span className="position-card-chip">
                                                        {chip.label}
                                                    </span>
                                                </Tooltip>
                                            ))}
                                        </div>
                                    )}

                                    {callouts.length > 0 && (
                                        <div className="position-card-callouts">
                                            {callouts.map((callout) => (
                                                <div key={`${team.team_id || team.owner}-${callout.label}`} className="position-card-callout">
                                                    <strong>{callout.value}</strong>
                                                    <span>{callout.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>
        </CopyableContainer>
    );
};

export default TeamPositionCards;
